import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, Package, RotateCcw } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Checkbox from '../ui/Checkbox';
import Table from '../ui/Table';
import Icon from '../ui/Icon';
import { clean } from '../shell/copy';

// Admin → Project downloads, on src/ui (DESIGN rev 2.1 §3.1 Table). Bulk export of a project's scripts (one file
// per script, each in its own format template) and videos (live / AI / raw footage) for handoff. Scripts and
// videos are downloaded and reported separately; there is no combined output. Same calls as before.

const QUAL_STATUSES = ['I/P', 'Needs Review', 'Needs Revision', 'Approved', 'Backburner'];
const QUAL_LABEL = { 'I/P': 'In progress', 'Needs Review': 'Needs review', 'Needs Revision': 'Needs revision', Approved: 'Approved', Backburner: 'Backburner' };
const GB = 1024 ** 3;
const MB = 1024 ** 2;

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
}
function fmtSize(bytes) {
  if (bytes == null || bytes === '') return '';
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '';
  return n >= GB ? `${(n / GB).toFixed(2)} GB` : `${(n / MB).toFixed(1)} MB`;
}
// Pull the server-supplied filename out of a Content-Disposition header.
function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const m = /filename="?([^"]+)"?/.exec(header);
  return m ? m[1] : fallback;
}
function saveBlob(data, filename) {
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
const none = <span className="ui-text-3">None</span>;

// A header checkbox and one per row, with a visually hidden label each.
const pick = (label, checked, onChange) => <Checkbox label={<span className="ui-sr">{label}</span>} checked={checked} onChange={onChange} />;

export default function ClientDownloads() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  const client = clients.find(c => c.id === clientId);

  return (
    <div className="ui-stack ui-stack--loose">
      <section className="ui-section" aria-label="Project">
        <Select label="Project to export" className="ui-fit" value={clientId} onChange={e => setClientId(e.target.value)}
          placeholder="Choose a project" options={clients.map(c => ({ value: c.id, label: clean(c.name) }))} />
        {!clientId && <p className="ui-section__note">Choose a project to list its scripts and videos for download.</p>}
      </section>

      {clientId && (
        <>
          <ScriptsPanel key={`s-${clientId}`} clientId={clientId} client={client} />
          <VideosPanel key={`v-${clientId}`} clientId={clientId} client={client} />
        </>
      )}
    </div>
  );
}

// ── Scripts ───────────────────────────────────────────────────────────────────
function ScriptsPanel({ clientId, client }) {
  const [status, setStatus] = useState('');       // '' = All
  const [format, setFormat] = useState('csv');     // csv | doc
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [zipping, setZipping] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [err, setErr] = useState('');
  const [loadErr, setLoadErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setLoadErr('');
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await api.get(`/api/client-downloads/${clientId}/scripts${q}`);
      setScripts(r.data.scripts || []);
      setSelected(new Set());
    } catch (e) {
      setLoadErr(clean(e?.response?.data?.error) || 'The scripts did not load.');
      setScripts([]);
    } finally { setLoading(false); }
  }, [clientId, status]);

  useEffect(() => { load(); }, [load]);

  const allChecked = scripts.length > 0 && selected.size === scripts.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(scripts.map(s => s.id)));
  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function downloadZip() {
    if (selected.size === 0) return;
    setZipping(true); setErr('');
    try {
      const res = await api.post('/api/client-downloads/scripts/zip', { scriptIds: [...selected], format }, { responseType: 'blob' });
      saveBlob(res.data, filenameFromDisposition(res.headers['content-disposition'], `${client?.initials || 'client'}-scripts.zip`));
    } catch {
      setErr('The scripts zip did not build. Try again.');
    } finally { setZipping(false); }
  }

  async function downloadReport() {
    setReporting(true); setErr('');
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : '';
      const res = await api.get(`/api/client-downloads/${clientId}/scripts/report${q}`, { responseType: 'blob' });
      saveBlob(res.data, filenameFromDisposition(res.headers['content-disposition'], `${client?.initials || 'client'}-scripts-report.csv`));
    } catch {
      setErr('The scripts report did not build. Try again.');
    } finally { setReporting(false); }
  }

  const columns = [
    { key: 'pick', label: pick('Select every script', allChecked, toggleAll), width: 48, render: s => pick(`Select ${clean(s.script_name)}`, selected.has(s.id), () => toggle(s.id)) },
    { key: 'name', label: 'Script name', width: '36%', render: s => clean(s.script_name) },
    { key: 'format', label: 'Format', render: s => s.format || none },
    { key: 'writer', label: 'Writer', render: s => clean(s.writer_name) },
    { key: 'date', label: 'Date written', render: s => <span className="ui-num">{fmtDate(s.date_entered)}</span> },
    { key: 'status', label: 'Status', render: s => QUAL_LABEL[s.qual_status] || s.qual_status },
  ];

  return (
    <section className="ui-section" aria-labelledby="dl-scripts">
      <h2 className="ui-section__title" id="dl-scripts">Scripts</h2>
      <div className="ui-toolbar">
        <Select aria-label="Script status" value={status} onChange={e => setStatus(e.target.value)}
          options={[{ value: '', label: 'All statuses' }, ...QUAL_STATUSES.map(s => ({ value: s, label: QUAL_LABEL[s] }))]} />
        <span className="ui-dense ui-text-2" id="dl-format">Format</span>
        <div className="ui-seg" role="group" aria-labelledby="dl-format">
          {['csv', 'doc'].map(f => <Button key={f} variant="quiet" aria-pressed={format === f} onClick={() => setFormat(f)}>{f.toUpperCase()}</Button>)}
        </div>
        <span className="ui-spacer" />
        <Button variant="secondary" icon={Download} loading={reporting} onClick={downloadReport}>Scripts report (CSV)</Button>
        <Button variant="primary" icon={Download} loading={zipping} disabled={selected.size === 0} onClick={downloadZip}>
          {`Download ${selected.size} script${selected.size === 1 ? '' : 's'} (zip)`}
        </Button>
      </div>
      {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      <div className="ui-region">
        <Table caption="Scripts to download" columns={columns} rows={scripts} maxHeight={420}
          state={loadErr ? 'error' : loading ? 'loading' : 'data'}
          error={{ title: 'Scripts did not load', message: loadErr, onRetry: load }}
          empty={{ message: 'No scripts match this status. Pick All statuses to see every script.' }} />
      </div>
    </section>
  );
}

// ── Videos ────────────────────────────────────────────────────────────────────
const TYPE_LABELS = { live: 'Live videos', ai: 'AI videos', footage: 'Raw footage' };
const TYPE_BADGE = { live: 'Live', ai: 'AI', footage: 'Raw footage' };

function VideosPanel({ clientId, client }) {
  const [types, setTypes] = useState({ live: true, ai: false, footage: false });
  const [groupSize, setGroupSize] = useState(10);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [groups, setGroups] = useState([]);      // [{ id, label, items, sizeBytes, status, downloadUrl }]
  const [building, setBuilding] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [err, setErr] = useState('');
  const [loadErr, setLoadErr] = useState('');
  const cancelRef = useRef(false);

  const activeTypes = Object.keys(types).filter(t => types[t]);

  const load = useCallback(async () => {
    if (activeTypes.length === 0) { setVideos([]); return; }
    setLoading(true); setLoadErr('');
    try {
      const r = await api.get(`/api/client-downloads/${clientId}/videos?types=${activeTypes.join(',')}`);
      setVideos(r.data.videos || []);
      setSelected(new Set());
    } catch (e) {
      setLoadErr(clean(e?.response?.data?.error) || 'The videos did not load.');
      setVideos([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, JSON.stringify(types)]);

  useEffect(() => { load(); }, [load]);

  const allChecked = videos.length > 0 && selected.size === videos.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(videos.map(v => v.id)));
  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleType = t => setTypes(prev => ({ ...prev, [t]: !prev[t] }));

  // Selection, in list order.
  const selectedVideos = videos.filter(v => selected.has(v.id));
  const totalBytes = selectedVideos.reduce((sum, v) => sum + (Number(v.file_size_bytes) || 0), 0);
  const g = Math.max(1, Math.min(50, Number(groupSize) || 1));
  const numGroups = Math.ceil(selectedVideos.length / g);

  // Chunk selection into groups of g (list order).
  function chunkGroups() {
    const chunks = [];
    for (let i = 0; i < selectedVideos.length; i += g) {
      const items = selectedVideos.slice(i, i + g);
      chunks.push({
        id: `grp-${i / g + 1}`,
        label: `Group ${i / g + 1} of ${Math.ceil(selectedVideos.length / g)}`,
        items,
        sizeBytes: items.reduce((s, v) => s + (Number(v.file_size_bytes) || 0), 0),
        status: 'pending',
        downloadUrl: null,
      });
    }
    return chunks;
  }

  // Build one group: create the zip job, poll until complete/failed.
  async function buildOneGroup(group) {
    setGroups(prev => prev.map(x => (x.id === group.id ? { ...x, status: 'building', downloadUrl: null } : x)));
    try {
      const items = group.items.map(v => ({ type: v.type === 'footage' ? 'raw_footage' : 'video_asset', id: v.id }));
      const res = await api.post('/api/downloads/zip', { items });
      const { jobId } = res.data;
      // Poll (mirrors the card zip flow).
      const url = await new Promise((resolve, reject) => {
        const iv = setInterval(async () => {
          if (cancelRef.current) { clearInterval(iv); reject(new Error('cancelled')); return; }
          try {
            const poll = await api.get(`/api/downloads/zip/${jobId}`);
            if (poll.data.status === 'complete') { clearInterval(iv); resolve(poll.data.downloadUrl); }
            else if (poll.data.status === 'failed') { clearInterval(iv); reject(new Error('failed')); }
          } catch (e) { clearInterval(iv); reject(e); }
        }, 3000);
      });
      setGroups(prev => prev.map(x => (x.id === group.id ? { ...x, status: 'complete', downloadUrl: url } : x)));
      return true;
    } catch {
      setGroups(prev => prev.map(x => (x.id === group.id ? { ...x, status: 'failed', downloadUrl: null } : x)));
      return false;
    }
  }

  // Build all groups one after another, at most one in flight.
  async function buildGroups() {
    if (selectedVideos.length === 0) return;
    cancelRef.current = false;
    const chunks = chunkGroups();
    setGroups(chunks);
    setBuilding(true); setErr('');
    for (const group of chunks) {
      if (cancelRef.current) break;
      await buildOneGroup(group);   // a failed group does not block the rest
    }
    setBuilding(false);
  }

  async function rebuildGroup(groupId) {
    const group = groups.find(x => x.id === groupId);
    if (!group) return;
    await buildOneGroup(group);
  }

  async function downloadReport() {
    if (activeTypes.length === 0) return;
    setReporting(true); setErr('');
    try {
      const res = await api.get(`/api/client-downloads/${clientId}/videos/report?types=${activeTypes.join(',')}`, { responseType: 'blob' });
      saveBlob(res.data, filenameFromDisposition(res.headers['content-disposition'], `${client?.initials || 'client'}-videos-report.csv`));
    } catch {
      setErr('The videos report did not build. Try again.');
    } finally { setReporting(false); }
  }

  const columns = [
    { key: 'pick', label: pick('Select every video', allChecked, toggleAll), width: 48, render: v => pick(`Select ${clean(v.title)}`, selected.has(v.id), () => toggle(v.id)) },
    { key: 'title', label: 'Title', width: '36%', render: v => clean(v.title) },
    { key: 'type', label: 'Type', render: v => TYPE_BADGE[v.type] },
    { key: 'status', label: 'Status', render: v => (v.status ? clean(v.status) : none) },
    { key: 'date', label: 'Date uploaded', render: v => <span className="ui-num">{fmtDate(v.uploaded_at)}</span> },
    { key: 'size', label: 'Size', align: 'right', render: v => <span className="ui-num">{fmtSize(v.file_size_bytes)}</span> },
  ];

  return (
    <section className="ui-section" aria-labelledby="dl-videos">
      <h2 className="ui-section__title" id="dl-videos">Videos</h2>
      <div className="ui-toolbar">
        {Object.keys(TYPE_LABELS).map(t => <Checkbox key={t} label={TYPE_LABELS[t]} checked={types[t]} onChange={() => toggleType(t)} />)}
        <Input aria-label="Videos per zip file" type="number" min={1} max={50} value={groupSize} onChange={e => setGroupSize(e.target.value)} className="ui-fit--narrow" />
        <span className="ui-dense ui-text-2">videos per zip file</span>
        <span className="ui-spacer" />
        <Button variant="secondary" icon={Download} loading={reporting} disabled={activeTypes.length === 0} onClick={downloadReport}>Videos report (CSV)</Button>
      </div>
      {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}

      {activeTypes.length === 0 ? (
        <p className="ui-section__note">Tick at least one video type to list videos.</p>
      ) : (
        <>
          <div className="ui-region">
            <Table caption="Videos to download" columns={columns} rows={videos} maxHeight={420}
              state={loadErr ? 'error' : loading ? 'loading' : 'data'}
              error={{ title: 'Videos did not load', message: loadErr, onRetry: load }}
              empty={{ message: 'No videos of the ticked types. Tick another type to widen the list.' }} />
          </div>

          {videos.length > 0 && (
            <div className="ui-toolbar">
              <span className="ui-dense ui-text-2" aria-live="polite">
                {selectedVideos.length === 0
                  ? 'No videos selected.'
                  : `${selectedVideos.length} video${selectedVideos.length === 1 ? '' : 's'} selected: ${numGroups} zip file${numGroups === 1 ? '' : 's'} of up to ${g}, about ${(totalBytes / GB).toFixed(2)} GB in total`}
              </span>
              <span className="ui-spacer" />
              <Button variant="primary" icon={Package} loading={building} disabled={selectedVideos.length === 0} onClick={buildGroups}>Build zip groups</Button>
            </div>
          )}

          {groups.length > 0 && (
            <div className="ui-region ui-list" aria-label="Zip groups">
              {groups.map(grp => (
                <div key={grp.id} className="ui-row">
                  <span className="ui-row__main ui-dense">
                    {grp.label} ({grp.items.length} video{grp.items.length === 1 ? '' : 's'}, {fmtSize(grp.sizeBytes)})
                  </span>
                  <span className="ui-row__trail ui-cluster">
                    {grp.status === 'building' && <Badge tone="info">Building</Badge>}
                    {grp.status === 'pending' && <span className="ui-dense ui-text-3">Queued</span>}
                    {grp.status === 'complete' && <>
                      <span className="ui-dense ui-text-3">Link expires in 1 hour</span>
                      <a className="ui ui-btn ui-btn--primary" href={grp.downloadUrl} rel="noopener"><Icon icon={Download} />Download zip</a>
                      <Button variant="quiet" icon={RotateCcw} onClick={() => rebuildGroup(grp.id)}>Rebuild</Button>
                    </>}
                    {grp.status === 'failed' && <>
                      <Badge tone="danger">Failed</Badge>
                      <Button variant="quiet" icon={RotateCcw} onClick={() => rebuildGroup(grp.id)}>Rebuild</Button>
                    </>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
